import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { track } from "@vercel/analytics";
import { DIAL_CODES, dialCodeFor } from "../../data/dialCodes";
import { COUNTRIES } from "../../data/countries";
import { REFERRAL_OPTIONS, ROLE_OPTIONS, formatMoney } from "./content";
import { fieldId, needsProperties, orderedErrorKeys, validateForm } from "./validation";

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_live_51SDWk7GLCy9PVleCNU1Eow6f4yVZDhCTbLH0vNdUKbBqk05OsixJ2o17BfOwgXv5G69VsQe64z13tpB0nCtkwGrF00w3Ju6FYI";

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

export const DRAFT_KEY = "postaway_inner_circle_draft_v1";

const newKey = () => Math.random().toString(36).slice(2, 10);
const emptyProperty = () => ({ key: newKey(), name: "", city: "", region: "", country: "" });
const emptyHandle = () => ({ key: newKey(), value: "" });

const ROLE_LABELS = { host: "Host", creator: "Creator", both: "Host & Creator" };

const safeTrack = (name, props) => {
  try {
    track(name, props);
  } catch (_) {}
};

function initialForm() {
  let accountEmail = "";
  try {
    accountEmail = localStorage.getItem("postaway_email") || "";
  } catch (_) {}
  const base = {
    firstName: "",
    lastName: "",
    role: "",
    properties: [],
    primaryInstagram: "",
    hasAdditionalHandles: "",
    additionalInstagram: [],
    email: accountEmail,
    phoneCountry: "US",
    phoneNumber: "",
    referralSource: "",
    referralDetail: "",
  };
  try {
    const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    if (!draft || typeof draft !== "object") return base;
    return {
      ...base,
      ...draft,
      properties: Array.isArray(draft.properties)
        ? draft.properties.map((p) => ({ ...emptyProperty(), ...p, key: p.key || newKey() }))
        : [],
      additionalInstagram: Array.isArray(draft.additionalInstagram)
        ? draft.additionalInstagram.map((h) => ({ ...emptyHandle(), ...h, key: h.key || newKey() }))
        : [],
    };
  } catch (_) {
    return base;
  }
}

const focusById = (id) => {
  window.setTimeout(() => {
    const el = document.getElementById(id);
    if (el) el.focus();
  }, 0);
};

const Field = ({ id, label, optional, required, hint, error, children, className = "" }) => (
  <div className={`ic-field ${error ? "has-error" : ""} ${className}`.trim()}>
    <label htmlFor={id} className="ic-label">
      {label}
      {optional && <span className="ic-optional"> (optional)</span>}
      {required && <span className="ic-required" aria-hidden="true"> *</span>}
    </label>
    {hint && (
      <p id={`${id}-hint`} className="ic-hint">
        {hint}
      </p>
    )}
    {children}
    {error && (
      <p id={`${id}-error`} className="ic-error">
        {error}
      </p>
    )}
  </div>
);

const describedBy = (id, { hint, error }) =>
  [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;

export const ResendWelcome = ({ initialEmail = "", supportEmail, compact = false }) => {
  const [open, setOpen] = useState(!compact);
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus("Enter the email address you registered with.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/inner-circle/resend-welcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      setStatus(data?.message || `Something went wrong. Contact ${supportEmail} and we'll help.`);
    } catch (_) {
      setStatus(`Something went wrong. Contact ${supportEmail} and we'll help.`);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="ic-text-btn" onClick={() => setOpen(true)} aria-expanded="false">
        Already enrolled? Resend my welcome email
      </button>
    );
  }

  return (
    <form className="ic-resend" onSubmit={send} noValidate>
      <label htmlFor="ic-resend-email" className="ic-label">
        Resend my welcome email
      </label>
      <div className="ic-resend-row">
        <input
          id="ic-resend-email"
          className="ic-input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        <button type="submit" className="ic-btn secondary" disabled={sending}>
          {sending ? "Sending…" : "Resend"}
        </button>
      </div>
      <p className="ic-hint" role="status" aria-live="polite">
        {status}
      </p>
    </form>
  );
};

const RegistrationForm = ({ cohort, enrollment }) => {
  const supportEmail = cohort?.supportEmail || "jamie@postaway.com";
  const currency = cohort?.currency || "usd";
  const memberPrice = formatMoney(cohort?.memberPriceCents ?? 59900, currency);
  const regularPrice = formatMoney(cohort?.regularPriceCents ?? 79900, currency);

  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [phase, setPhase] = useState("form");
  const [quote, setQuote] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const startedRef = useRef(false);
  const sectionRef = useRef(null);
  const firstRender = useRef(true);

  const clientErrors = useMemo(() => validateForm(form), [form]);
  const errorFor = (key) => serverErrors[key] || ((submitAttempted || touched[key]) && clientErrors[key]) || "";
  const visibleErrorCount = submitAttempted
    ? Object.keys({ ...clientErrors, ...serverErrors }).length
    : 0;

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch (_) {}
  }, [form]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [phase]);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    safeTrack("ic_registration_started");
  };

  const clearServerError = (key) => {
    if (!serverErrors[key]) return;
    setServerErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const update = (key, value) => {
    markStarted();
    clearServerError(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const touch = (key) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const inputProps = (key, { hint } = {}) => {
    const id = fieldId(key);
    const error = errorFor(key);
    return {
      id,
      "aria-invalid": error ? "true" : undefined,
      "aria-describedby": describedBy(id, { hint, error }),
      onBlur: () => touch(key),
      className: `ic-input${error ? " has-error" : ""}`,
    };
  };

  const setRole = (role) => {
    markStarted();
    clearServerError("role");
    setForm((prev) => {
      const next = { ...prev, role };
      if (needsProperties(role) && !prev.properties.length) next.properties = [emptyProperty()];
      return next;
    });
    setAnnouncement(
      needsProperties(role)
        ? "Property details are now shown below. Add at least one property."
        : "Property details are not needed for creators."
    );
  };

  const updateProperty = (index, key, value) => {
    markStarted();
    clearServerError(`properties.${index}.${key}`);
    setForm((prev) => ({
      ...prev,
      properties: prev.properties.map((p, i) => (i === index ? { ...p, [key]: value } : p)),
    }));
  };

  const addProperty = () => {
    const row = emptyProperty();
    const index = form.properties.length;
    setForm((prev) => ({ ...prev, properties: [...prev.properties, row] }));
    setAnnouncement(`Property ${index + 1} added.`);
    focusById(fieldId(`properties.${index}.name`));
  };

  const removeProperty = (index) => {
    setForm((prev) => ({ ...prev, properties: prev.properties.filter((_, i) => i !== index) }));
    setServerErrors({});
    setAnnouncement(`Property ${index + 1} removed.`);
    focusById(index > 0 ? fieldId(`properties.${index - 1}.name`) : fieldId("properties"));
  };

  const setHasAdditional = (value) => {
    markStarted();
    clearServerError("hasAdditionalHandles");
    setForm((prev) => {
      const next = { ...prev, hasAdditionalHandles: value };
      if (value === "yes" && !prev.additionalInstagram.length) next.additionalInstagram = [emptyHandle()];
      return next;
    });
    setAnnouncement(value === "yes" ? "Additional handle fields are now shown below." : "");
  };

  const updateHandle = (index, value) => {
    markStarted();
    clearServerError(`additionalInstagram.${index}`);
    setForm((prev) => ({
      ...prev,
      additionalInstagram: prev.additionalInstagram.map((h, i) => (i === index ? { ...h, value } : h)),
    }));
  };

  const addHandle = () => {
    const index = form.additionalInstagram.length;
    setForm((prev) => ({ ...prev, additionalInstagram: [...prev.additionalInstagram, emptyHandle()] }));
    setAnnouncement(`Handle ${index + 2} added.`);
    focusById(fieldId(`additionalInstagram.${index}`));
  };

  const removeHandle = (index) => {
    setForm((prev) => ({ ...prev, additionalInstagram: prev.additionalInstagram.filter((_, i) => i !== index) }));
    setServerErrors({});
    setAnnouncement("Handle removed.");
    focusById(index > 0 ? fieldId(`additionalInstagram.${index - 1}`) : fieldId("additionalInstagram"));
  };

  const focusFirstError = useCallback(
    (errors) => {
      const [first] = orderedErrorKeys(form, errors);
      if (!first) return;
      if (first === "role") focusById(`${fieldId("role")}-${ROLE_OPTIONS[0].id}`);
      else if (first === "hasAdditionalHandles") focusById(`${fieldId("hasAdditionalHandles")}-yes`);
      else focusById(fieldId(first));
    },
    [form]
  );

  const buildPayload = () => ({
    firstName: form.firstName,
    lastName: form.lastName,
    role: form.role,
    properties: needsProperties(form.role)
      ? form.properties.map(({ name, city, region, country }) => ({ name, city, region, country }))
      : [],
    primaryInstagram: form.primaryInstagram,
    hasAdditionalHandles: form.hasAdditionalHandles === "yes",
    additionalInstagram: form.hasAdditionalHandles === "yes" ? form.additionalInstagram.map((h) => h.value) : [],
    email: form.email,
    phoneCountryCode: dialCodeFor(form.phoneCountry),
    phoneNumber: form.phoneNumber,
    referralSource: form.referralSource,
    referralDetail: form.referralDetail,
  });

  const closedMessage = (error) =>
    error === "enrollment_full"
      ? "This cohort just filled up."
      : "Enrollment isn't open right now.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setSubmitError("");
    if (Object.keys(clientErrors).length) {
      focusFirstError(clientErrors);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/inner-circle/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setQuote(data);
        setPhase("review");
        safeTrack("ic_registration_submitted", { role: form.role });
      } else if (data.error === "invalid_fields") {
        setServerErrors(data.errors || {});
        focusFirstError(data.errors || {});
      } else if (data.error === "already_enrolled") {
        setPhase("enrolled");
      } else if (data.error === "enrollment_closed" || data.error === "enrollment_full") {
        setSubmitError(`${closedMessage(data.error)} Questions? Contact ${supportEmail}.`);
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch (_) {
      setSubmitError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startCheckout = async () => {
    if (!quote?.registrationId) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch("/api/inner-circle/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ registrationId: quote.registrationId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok && data.clientSecret) {
        setQuote((prev) => ({ ...prev, priceTier: data.priceTier, amountCents: data.amountCents, currency: data.currency }));
        setClientSecret(data.clientSecret);
        setPhase("checkout");
        safeTrack("ic_checkout_started", { role: form.role, tier: data.priceTier });
      } else if (data.error === "already_enrolled") {
        setPhase("enrolled");
      } else if (data.error === "enrollment_closed" || data.error === "enrollment_full") {
        setSubmitError(`${closedMessage(data.error)} Questions? Contact ${supportEmail}.`);
      } else {
        setSubmitError(`We couldn't start checkout. Please try again, or contact ${supportEmail}.`);
      }
    } catch (_) {
      setSubmitError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const backToForm = (focusKey) => {
    setClientSecret("");
    setSubmitError("");
    setPhase("form");
    if (focusKey) focusById(fieldId(focusKey));
  };

  if (enrollment === "loading") {
    return (
      <div className="ic-card ic-status-card" role="status">
        <div className="ic-spinner" aria-hidden="true" />
        Loading enrollment…
      </div>
    );
  }

  if (enrollment !== "open" && phase === "form") {
    return (
      <div className="ic-card ic-status-card">
        <h3 className="ic-card-title">
          {enrollment === "full" ? "This cohort is full" : "Enrollment opens soon"}
        </h3>
        <p className="ic-card-text">
          {enrollment === "full"
            ? "Every spot in the January 2027 cohort has been claimed."
            : "Paid enrollment for the January 2027 cohort isn't open yet."}{" "}
          Questions? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </p>
        <ResendWelcome supportEmail={supportEmail} compact />
      </div>
    );
  }

  const isMemberRate = quote?.priceTier === "member";
  const quoteAmount = formatMoney(quote?.amountCents, quote?.currency || currency);

  return (
    <div className="ic-register" ref={sectionRef}>
      <p className="ic-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {phase === "enrolled" && (
        <div className="ic-card ic-status-card" role="status">
          <h3 className="ic-card-title">You're already enrolled</h3>
          <p className="ic-card-text">
            <strong>{form.email.trim()}</strong> already has a paid spot in this cohort, so there's nothing more to pay.
            We can resend your welcome email with the GroupMe invite.
          </p>
          <ResendWelcome initialEmail={form.email.trim()} supportEmail={supportEmail} />
          <p className="ic-hint">
            Need help? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </p>
          <button type="button" className="ic-text-btn" onClick={() => backToForm("email")}>
            Use a different email
          </button>
        </div>
      )}

      {phase === "review" && quote && (
        <div className="ic-card ic-review">
          <h3 className="ic-card-title">Review your tuition</h3>
          <dl className="ic-review-list">
            <div>
              <dt>Name</dt>
              <dd>
                {form.firstName.trim()} {form.lastName.trim()}
              </dd>
            </div>
            <div>
              <dt>Joining as</dt>
              <dd>{ROLE_LABELS[form.role]}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd className="ic-break">{form.email.trim()}</dd>
            </div>
          </dl>

          <div className={`ic-quote ${isMemberRate ? "is-member" : ""}`}>
            <div className="ic-quote-label">{isMemberRate ? "Postaway member rate applied" : "Regular tuition"}</div>
            <div className="ic-quote-amount">
              {isMemberRate && (
                <s className="ic-quote-was" aria-label={`Regular tuition ${regularPrice}`}>
                  {regularPrice}
                </s>
              )}
              <span>{quoteAmount}</span>
            </div>
            <div className="ic-quote-note">One-time payment for the January 2027 cohort. No automatic renewal.</div>
          </div>

          {!isMemberRate && (
            <div className="ic-member-hint">
              <strong>Postaway member?</strong> We couldn't match <strong className="ic-break">{form.email.trim()}</strong> to
              an active Postaway membership. Use the email on your Postaway account to get the {memberPrice} member rate.{" "}
              <button type="button" className="ic-text-btn inline" onClick={() => backToForm("email")}>
                Edit email
              </button>
            </div>
          )}

          {submitError && (
            <p className="ic-form-error" role="alert">
              {submitError}
            </p>
          )}

          <div className="ic-actions">
            <button type="button" className="ic-btn primary" onClick={startCheckout} disabled={submitting}>
              {submitting ? "Preparing secure checkout…" : `Continue to secure payment · ${quoteAmount}`}
            </button>
            <button type="button" className="ic-btn secondary" onClick={() => backToForm()}>
              Edit details
            </button>
          </div>
          <p className="ic-trust">Secure checkout powered by Stripe</p>
        </div>
      )}

      {phase === "checkout" && clientSecret && (
        <div className="ic-checkout">
          <div className="ic-checkout-summary">
            <span>
              Inner Circle · {isMemberRate ? "Member rate" : "Regular tuition"} · {quoteAmount}
            </span>
            <button type="button" className="ic-text-btn" onClick={() => backToForm()}>
              Edit details
            </button>
          </div>
          <div className="ic-checkout-frame">
            <EmbeddedCheckoutProvider key={clientSecret} stripe={getStripe()} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      )}

      {phase === "form" && (
        <form className="ic-form" onSubmit={handleSubmit} noValidate>
          <p className="ic-hint">
            Fields marked <span aria-hidden="true">*</span>
            <span className="ic-sr-only">with an asterisk</span> are required.
          </p>

          {visibleErrorCount > 0 && (
            <div className="ic-form-error" role="alert">
              Please fix {visibleErrorCount === 1 ? "1 field" : `${visibleErrorCount} fields`} below.
            </div>
          )}

          <div className="ic-grid-2">
            <Field id={fieldId("firstName")} label="First name" required error={errorFor("firstName")}>
              <input
                {...inputProps("firstName")}
                type="text"
                autoComplete="given-name"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
              />
            </Field>
            <Field id={fieldId("lastName")} label="Last name" required error={errorFor("lastName")}>
              <input
                {...inputProps("lastName")}
                type="text"
                autoComplete="family-name"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
              />
            </Field>
          </div>

          <fieldset
            className={`ic-fieldset ${errorFor("role") ? "has-error" : ""}`}
            aria-describedby={errorFor("role") ? `${fieldId("role")}-error` : undefined}
          >
            <legend className="ic-label">
              Are you a host or a creator?<span className="ic-required" aria-hidden="true"> *</span>
            </legend>
            <div className="ic-choice-grid">
              {ROLE_OPTIONS.map((opt) => (
                <label key={opt.id} className={`ic-choice ${form.role === opt.id ? "selected" : ""}`}>
                  <input
                    type="radio"
                    id={`${fieldId("role")}-${opt.id}`}
                    name="ic-role"
                    value={opt.id}
                    checked={form.role === opt.id}
                    onChange={() => setRole(opt.id)}
                    aria-controls={needsProperties(opt.id) ? "ic-properties-group" : undefined}
                  />
                  <span className="ic-choice-text">
                    <span className="ic-choice-label">{opt.label}</span>
                    <span className="ic-choice-hint">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {errorFor("role") && (
              <p id={`${fieldId("role")}-error`} className="ic-error">
                {errorFor("role")}
              </p>
            )}
          </fieldset>

          {needsProperties(form.role) && (
            <fieldset
              id="ic-properties-group"
              className={`ic-fieldset ic-subsection ${errorFor("properties") ? "has-error" : ""}`}
              aria-describedby="ic-properties-hint"
            >
              <legend className="ic-label">
                Your properties<span className="ic-required" aria-hidden="true"> *</span>
              </legend>
              <p id="ic-properties-hint" className="ic-hint">
                Add each property you host, with its name and general location. No street address needed.
              </p>
              {form.properties.map((p, i) => (
                <div key={p.key} className="ic-repeat-row" role="group" aria-labelledby={`ic-property-label-${p.key}`}>
                  <div className="ic-repeat-head">
                    <span id={`ic-property-label-${p.key}`} className="ic-repeat-title">
                      Property {i + 1}
                    </span>
                    {form.properties.length > 1 && (
                      <button
                        type="button"
                        className="ic-text-btn danger"
                        onClick={() => removeProperty(i)}
                        aria-label={`Remove property ${i + 1}${p.name ? `, ${p.name}` : ""}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <Field
                    id={fieldId(`properties.${i}.name`)}
                    label="Property name"
                    required
                    error={errorFor(`properties.${i}.name`)}
                  >
                    <input
                      {...inputProps(`properties.${i}.name`)}
                      type="text"
                      value={p.name}
                      onChange={(e) => updateProperty(i, "name", e.target.value)}
                    />
                  </Field>
                  <div className="ic-grid-3">
                    <Field id={fieldId(`properties.${i}.city`)} label="City" required error={errorFor(`properties.${i}.city`)}>
                      <input
                        {...inputProps(`properties.${i}.city`)}
                        type="text"
                        autoComplete="off"
                        value={p.city}
                        onChange={(e) => updateProperty(i, "city", e.target.value)}
                      />
                    </Field>
                    <Field id={fieldId(`properties.${i}.region`)} label="State / region" optional>
                      <input
                        {...inputProps(`properties.${i}.region`)}
                        type="text"
                        autoComplete="off"
                        value={p.region}
                        onChange={(e) => updateProperty(i, "region", e.target.value)}
                      />
                    </Field>
                    <Field
                      id={fieldId(`properties.${i}.country`)}
                      label="Country"
                      required
                      error={errorFor(`properties.${i}.country`)}
                    >
                      <input
                        {...inputProps(`properties.${i}.country`)}
                        type="text"
                        list="ic-country-list"
                        autoComplete="off"
                        value={p.country}
                        onChange={(e) => updateProperty(i, "country", e.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              ))}
              <datalist id="ic-country-list">
                {COUNTRIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <button
                type="button"
                id={fieldId("properties")}
                className="ic-btn secondary ic-add-btn"
                onClick={addProperty}
                aria-describedby={errorFor("properties") ? `${fieldId("properties")}-error` : undefined}
              >
                + Add another property
              </button>
              {errorFor("properties") && (
                <p id={`${fieldId("properties")}-error`} className="ic-error">
                  {errorFor("properties")}
                </p>
              )}
            </fieldset>
          )}

          <Field
            id={fieldId("primaryInstagram")}
            label="Primary Instagram handle"
            required
            hint="Your handle, @handle or Instagram profile link."
            error={errorFor("primaryInstagram")}
          >
            <input
              {...inputProps("primaryInstagram", { hint: true })}
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              placeholder="@yourhandle"
              value={form.primaryInstagram}
              onChange={(e) => update("primaryInstagram", e.target.value)}
            />
          </Field>

          <fieldset
            className={`ic-fieldset ${errorFor("hasAdditionalHandles") ? "has-error" : ""}`}
            aria-describedby={errorFor("hasAdditionalHandles") ? `${fieldId("hasAdditionalHandles")}-error` : undefined}
          >
            <legend className="ic-label">
              Do you have additional Instagram handles?<span className="ic-required" aria-hidden="true"> *</span>
            </legend>
            <div className="ic-inline-choices">
              {[
                { id: "yes", label: "Yes" },
                { id: "no", label: "No" },
              ].map((opt) => (
                <label key={opt.id} className={`ic-pill ${form.hasAdditionalHandles === opt.id ? "selected" : ""}`}>
                  <input
                    type="radio"
                    id={`${fieldId("hasAdditionalHandles")}-${opt.id}`}
                    name="ic-has-additional"
                    value={opt.id}
                    checked={form.hasAdditionalHandles === opt.id}
                    onChange={() => setHasAdditional(opt.id)}
                    aria-controls={opt.id === "yes" ? "ic-additional-handles" : undefined}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {errorFor("hasAdditionalHandles") && (
              <p id={`${fieldId("hasAdditionalHandles")}-error`} className="ic-error">
                {errorFor("hasAdditionalHandles")}
              </p>
            )}
          </fieldset>

          {form.hasAdditionalHandles === "yes" && (
            <fieldset id="ic-additional-handles" className="ic-fieldset ic-subsection">
              <legend className="ic-label">Additional Instagram handles</legend>
              {form.additionalInstagram.map((h, i) => {
                const key = `additionalInstagram.${i}`;
                const error = errorFor(key);
                return (
                  <div key={h.key} className={`ic-field ${error ? "has-error" : ""}`}>
                    <label htmlFor={fieldId(key)} className="ic-label ic-label-small">
                      Handle {i + 2}
                    </label>
                    <div className="ic-handle-row">
                      <input
                        {...inputProps(key)}
                        type="text"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                        placeholder="@anotherhandle"
                        value={h.value}
                        onChange={(e) => updateHandle(i, e.target.value)}
                      />
                      <button
                        type="button"
                        className="ic-text-btn danger"
                        onClick={() => removeHandle(i)}
                        aria-label={`Remove handle ${i + 2}`}
                      >
                        Remove
                      </button>
                    </div>
                    {error && (
                      <p id={`${fieldId(key)}-error`} className="ic-error">
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                id={fieldId("additionalInstagram")}
                className="ic-btn secondary ic-add-btn"
                onClick={addHandle}
                aria-describedby={errorFor("additionalInstagram") ? `${fieldId("additionalInstagram")}-error` : undefined}
              >
                + Add another handle
              </button>
              {errorFor("additionalInstagram") && (
                <p id={`${fieldId("additionalInstagram")}-error`} className="ic-error">
                  {errorFor("additionalInstagram")}
                </p>
              )}
            </fieldset>
          )}

          <Field
            id={fieldId("email")}
            label="Email address"
            required
            hint={`Postaway member? Use the email on your Postaway account to get the ${memberPrice} member rate. We'll send your enrollment confirmation and program updates here.`}
            error={errorFor("email")}
          >
            <input
              {...inputProps("email", { hint: true })}
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck="false"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </Field>

          <Field
            id={fieldId("phoneNumber")}
            label="Cell number"
            required
            hint="For program contact only. We won't send promotional texts."
            error={errorFor("phoneNumber")}
          >
            <div className="ic-phone">
              <select
                id="ic-phoneCountry"
                className="ic-input ic-select"
                aria-label="Country calling code"
                autoComplete="tel-country-code"
                value={form.phoneCountry}
                onChange={(e) => update("phoneCountry", e.target.value)}
              >
                {DIAL_CODES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
              <input
                {...inputProps("phoneNumber", { hint: true })}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder={form.phoneCountry === "US" || form.phoneCountry === "CA" ? "(555) 123-4567" : ""}
                value={form.phoneNumber}
                onChange={(e) => update("phoneNumber", e.target.value)}
              />
            </div>
          </Field>

          <Field id={fieldId("referralSource")} label="How did you hear about us?" required error={errorFor("referralSource")}>
            <select
              {...inputProps("referralSource")}
              className={`ic-input ic-select${errorFor("referralSource") ? " has-error" : ""}`}
              value={form.referralSource}
              onChange={(e) => update("referralSource", e.target.value)}
            >
              <option value="">Choose one</option>
              {REFERRAL_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          {form.referralSource && (
            <Field
              id={fieldId("referralDetail")}
              label={form.referralSource === "other" ? "Please explain" : "Anything else to share?"}
              optional={form.referralSource !== "other"}
              required={form.referralSource === "other"}
              hint={form.referralSource === "referral" ? "Who referred you? A name or handle is perfect." : undefined}
              error={errorFor("referralDetail")}
            >
              <input
                {...inputProps("referralDetail", { hint: form.referralSource === "referral" })}
                type="text"
                maxLength={500}
                value={form.referralDetail}
                onChange={(e) => update("referralDetail", e.target.value)}
              />
            </Field>
          )}

          {submitError && (
            <p className="ic-form-error" role="alert">
              {submitError}
            </p>
          )}

          <button type="submit" className="ic-btn primary ic-submit" disabled={submitting}>
            {submitting ? "Saving your registration…" : "Review tuition"}
          </button>
          <p className="ic-trust">You'll see your final tuition before you pay.</p>

          <div className="ic-form-footer">
            <ResendWelcome supportEmail={supportEmail} compact />
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrationForm;
